import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  PencilIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const CreditNoteList = ({ onTabChange }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloadDropdown, setDownloadDropdown] = useState(null);
  const [printDropdown, setPrintDropdown] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Fetch credit notes
  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      
      // Check if JWT token exists
      const token = localStorage.getItem('jwtToken');
      if (!token) {
        setError(t('creditNoteList.noTokenError'));
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter }),
        ...(partnerFilter && { partnerId: partnerFilter }),
        ...(companyFilter && { companyId: companyFilter })
      });

      const response = await fetch(`/api/finance/credit-notes?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // JWT expired or invalid
        setError(t('creditNoteList.authenticationError'));
        // Clear the expired token
        localStorage.removeItem('jwtToken');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || t('creditNoteList.failedToFetchCreditNotes'));
      }

      const data = await response.json();
      setCreditNotes(data.creditNotes || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching credit notes:', error);
      if (error.message.includes('jwt') || error.message.includes('token')) {
        setError(t('creditNoteList.authenticationError'));
      } else {
        setError(error.message || t('creditNoteList.failedToLoadCreditNotes'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch partners for filter - only active partners
  const fetchPartners = async () => {
    try {
      const response = await fetch('/api/finance/partners?activeOnly=true', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      
      if (response.status === 401) {
        // JWT expired or invalid
        localStorage.removeItem('jwtToken');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setPartners(data.partners || data || []);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  // Fetch companies for filter
  const fetchCompanies = async () => {
    try {
      const response = await fetch('/api/finance/companies', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      
      if (response.status === 401) {
        // JWT expired or invalid
        localStorage.removeItem('jwtToken');
        return;
      }
      
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || data || []);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // Helper function to format dates
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('bg-BG');
  };

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (!amount) return '0.00 лв.';
    return new Intl.NumberFormat('bg-BG', {
      style: 'currency',
      currency: 'BGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Helper function to get status badge with translation
  const getStatusBadge = (status) => {
    if (!status) return '-';
    
    const statusConfig = {
      'Draft': {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        icon: <DocumentTextIcon className="h-4 w-4" />
      },
      'Sent': {
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        icon: <ArrowDownTrayIcon className="h-4 w-4" />
      },
      'Paid': {
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        icon: <CheckCircleIcon className="h-4 w-4" />
      },
      'Partial': {
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-800',
        icon: <CurrencyDollarIcon className="h-4 w-4" />
      },
      'Overdue': {
        bgColor: 'bg-red-100',
        textColor: 'text-red-800',
        icon: <ExclamationTriangleIcon className="h-4 w-4" />
      },
      'Cancelled': {
        bgColor: 'bg-gray-100',
        textColor: 'text-gray-800',
        icon: <XMarkIcon className="h-4 w-4" />
      }
    };

    const config = statusConfig[status] || statusConfig['Draft'];
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        {config.icon}
        <span className="ml-1">{t(`creditNoteList.status${status}`)}</span>
      </span>
    );
  };

  // Handle download
  const handleDownload = async (id, creditNoteNumber, type) => {
    try {
      const response = await fetch(`/api/finance/credit-notes/${id}/pdf?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${creditNoteNumber}_${type.toLowerCase()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setDownloadDropdown(null);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
      setError(t('creditNoteList.downloadFailed'));
    }
  };

  // Handle print
  const handlePrint = async (id, creditNoteNumber, type) => {
    try {
      const response = await fetch(`/api/finance/credit-notes/${id}/pdf?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        // Open PDF in new tab for preview and printing
        const newWindow = window.open(url, '_blank');
        
        // Clean up URL after a delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
        
        setPrintDropdown(null);
      } else {
        throw new Error('Print failed');
      }
    } catch (error) {
      console.error('Print error:', error);
      setError(t('creditNoteList.printFailed'));
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (window.confirm(t('creditNoteList.confirmDelete'))) {
      try {
        const response = await fetch(`/api/finance/credit-notes/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`
          }
        });
        
        if (response.ok) {
          setSuccessMessage(t('creditNoteList.deleteSuccess'));
          fetchCreditNotes();
        } else {
          throw new Error('Delete failed');
        }
      } catch (error) {
        console.error('Delete error:', error);
        setError(t('creditNoteList.deleteFailed'));
      }
    }
  };

  // Handle authentication refresh
  const handleAuthRefresh = () => {
    // Clear the expired token
    localStorage.removeItem('jwtToken');
    // Clear any existing errors
    setError(null);
    // Reload the page to trigger re-authentication
    window.location.reload();
  };

  // Retry fetching data after authentication error
  const retryFetch = () => {
    setError(null);
    fetchCreditNotes();
  };

  useEffect(() => {
    // Only fetch if we have a JWT token
    const token = localStorage.getItem('jwtToken');
    if (token) {
      fetchCreditNotes();
    } else {
      setError(t('creditNoteList.noTokenError'));
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter, partnerFilter, companyFilter]);

  useEffect(() => {
    // Only fetch if we have a JWT token
    const token = localStorage.getItem('jwtToken');
    if (token) {
      fetchPartners();
      fetchCompanies();
    }
  }, []);

  // Auto-dismiss success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (loading && creditNotes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check if we have a valid JWT token
  const token = localStorage.getItem('jwtToken');
  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-8 rounded-lg text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('creditNoteList.authenticationRequired')}</h2>
            <p className="text-red-700 mb-6">{t('creditNoteList.pleaseLoginToContinue')}</p>
            <button
              onClick={handleAuthRefresh}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              {t('creditNoteList.refreshAndLogin')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-full mx-auto">
        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center justify-between mx-4 sm:mx-6 lg:mx-8">
            <span>{successMessage}</span>
            <button
              onClick={() => setSuccessMessage('')}
              className="text-green-600 hover:text-green-800"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mx-4 sm:mx-6 lg:mx-8">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                  <span className="font-medium">{error}</span>
                </div>
                {error === t('creditNoteList.authenticationError') && (
                  <div className="mt-2">
                    <p className="text-sm text-red-700 mb-3">
                      {t('creditNoteList.pleaseLoginAgain')}
                    </p>
                    <button
                      onClick={handleAuthRefresh}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      {t('creditNoteList.refreshAndLogin')}
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 ml-4 flex-shrink-0"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6 px-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('creditNoteList.creditNotes')}</h1>
            <p className="text-gray-600">{t('creditNoteList.manageCreditNotesDescription')}</p>
          </div>
          <button 
            onClick={() => onTabChange('credit-note-create')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
          >
            <PlusIcon className="h-4 w-4" />
            <span>{t('creditNoteList.newCreditNote')}</span>
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6 mx-4 sm:mx-6 lg:mx-8">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('creditNoteList.searchCreditNotes')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('creditNoteList.allStatuses')}</option>
              <option value="Draft">{t('creditNoteList.statusDraft')}</option>
              <option value="Sent">{t('creditNoteList.statusSent')}</option>
              <option value="Paid">{t('creditNoteList.statusPaid')}</option>
              <option value="Partial">{t('creditNoteList.statusPartial')}</option>
              <option value="Overdue">{t('creditNoteList.statusOverdue')}</option>
              <option value="Cancelled">{t('creditNoteList.statusCancelled')}</option>
            </select>

            {/* Partner Filter */}
            <select
              value={partnerFilter}
              onChange={(e) => setPartnerFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('creditNoteList.allPartners')}</option>
              {partners.map((partner) => (
                <option key={partner._id} value={partner._id}>
                  {partner.name}
                </option>
              ))}
            </select>

            {/* Company Filter */}
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('creditNoteList.allCompanies')}</option>
              {companies.map((company) => (
                <option key={company._id} value={company._id}>
                  {company.name}
                </option>
              ))}
            </select>

            {/* Clear Filters */}
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setPartnerFilter('');
                setCompanyFilter('');
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              {t('creditNoteList.clearFilters')}
            </button>
          </div>
        </div>

        {/* Credit Notes Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mx-4 sm:mx-6 lg:mx-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <span className="text-sm text-gray-600">
              {total} {total === 1 ? t('creditNoteList.creditNote') : t('creditNoteList.creditNotes')} {t('creditNoteList.found')}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.creditNote')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.originalInvoice')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.partner')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.issueDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.dueDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.amount')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('creditNoteList.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {creditNotes.map((creditNote) => (
                  <tr key={creditNote._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {creditNote.creditNoteNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {creditNote.originalInvoiceNumber}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {creditNote.partnerId?.name || t('creditNoteList.notAvailable')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(creditNote.issueDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(creditNote.dueDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(creditNote.totalAmount)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(creditNote.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        {/* View */}
                        <button
                          onClick={() => navigate(`/admin/dashboard/credit-notes/${creditNote._id}`)}
                          className="text-blue-600 hover:text-blue-900"
                          title={t('creditNoteList.viewDetails')}
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>

                        {/* Download Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setDownloadDropdown(downloadDropdown === creditNote._id ? null : creditNote._id)}
                            className="text-green-600 hover:text-green-900"
                            title={t('creditNoteList.download')}
                          >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                          </button>
                          {downloadDropdown === creditNote._id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                              <div className="py-1">
                                <button
                                  onClick={() => handleDownload(creditNote._id, creditNote.creditNoteNumber, 'ORIGINAL')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {t('creditNoteList.downloadOriginal')}
                                </button>
                                <button
                                  onClick={() => handleDownload(creditNote._id, creditNote.creditNoteNumber, 'COPY')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {t('creditNoteList.downloadCopy')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Print Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setPrintDropdown(printDropdown === creditNote._id ? null : creditNote._id)}
                            className="text-purple-600 hover:text-purple-900"
                            title={t('creditNoteList.print')}
                          >
                            <PrinterIcon className="h-4 w-4" />
                          </button>
                          {printDropdown === creditNote._id && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                              <div className="py-1">
                                <button
                                  onClick={() => handlePrint(creditNote._id, creditNote.creditNoteNumber, 'ORIGINAL')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {t('creditNoteList.printOriginal')}
                                </button>
                                <button
                                  onClick={() => handlePrint(creditNote._id, creditNote.creditNoteNumber, 'COPY')}
                                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  {t('creditNoteList.printCopy')}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Edit */}
                        <button
                          onClick={() => onTabChange('credit-note-edit', { id: creditNote._id })}
                          className="text-indigo-600 hover:text-indigo-900"
                          title={t('creditNoteList.edit')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(creditNote._id)}
                          className="text-red-600 hover:text-red-900"
                          title={t('creditNoteList.delete')}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('creditNoteList.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('creditNoteList.next')}
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('creditNoteList.showing')} <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> {t('creditNoteList.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * 10, total)}</span> {t('creditNoteList.of')}{' '}
                    <span className="font-medium">{total}</span> {t('creditNoteList.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('creditNoteList.previous')}
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t('creditNoteList.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Empty State */}
        {creditNotes.length === 0 && !loading && (
          <div className="text-center py-12">
            <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('creditNoteList.noCreditNotes')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('creditNoteList.getStartedByCreatingNewCreditNote')}
            </p>
            <div className="mt-6">
              <button
                onClick={() => onTabChange('credit-note-create')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('creditNoteList.newCreditNote')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditNoteList; 